import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { Button } from '@/components/button';
import { Checkbox } from '@/components/checkbox';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { ItemsContext_OrderGuideItemFieldsFragment } from '@/types';
import { MultiStyleText } from '@/components/multi-style-text';
import { NOTES_MAX_LENGTH } from './constants';
import { TextArea } from '@/components/text-area';
import { useLayoutContext } from '@/contexts/layout-context';
import { useToasts } from '@/contexts/toast-context';

interface Props {
  onClose: () => void;
  onSave: (nextValue: string | null, persistNotes: boolean) => Promise<void>;
  orderGuideItem: ItemsContext_OrderGuideItemFieldsFragment;
}

export const EditNoteContent: React.FC<Props> = ({
  orderGuideItem,
  onSave,
  onClose,
}) => {
  const { isSmallLayout } = useLayoutContext();
  const { showErrorToast } = useToasts();
  const [styles] = useStyles();

  const [isLoading, setIsLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [noteValue, setNoteValue] = useState(
    orderGuideItem.order_guide_restaurant?.notes || '',
  );
  const [persistNotes, setPersistNotes] = useState(
    orderGuideItem.order_guide_restaurant?.persist_notes || false,
  );
  const showDeleteButton = Boolean(
    orderGuideItem.order_guide_restaurant?.notes,
  );
  return (
    <>
      <MultiStyleText
        defaultStyle={[
          TYPOGRAPHY.small,
          styles.subtitle,
          isSmallLayout ? null : styles.subtitleLarge,
        ]}
        emphasisStyle={FONTS.primarySemiBold}
      >
        {i18n.t('order_guide_note.description')}
      </MultiStyleText>
      <TextArea
        autoFocus
        caption={`${noteValue.length}/${NOTES_MAX_LENGTH}`}
        captionStyle={[
          styles.inputCaptionText,
          noteValue.length === NOTES_MAX_LENGTH
            ? styles.inputCaptionTextError
            : null,
        ]}
        containerStyle={styles.inputContainer}
        numberOfLines={4}
        onChangeText={(text) => {
          setNoteValue(text.slice(0, NOTES_MAX_LENGTH));
        }}
        placeholder={i18n.t('order_guide_note.placeholder')}
        selectTextOnFocus
        value={noteValue}
      />
      <Checkbox
        isSelected={persistNotes}
        label={i18n.t('order_guide_note.persist')}
        labelStyle={FONTS.primary}
        onSelect={() => setPersistNotes(!persistNotes)}
        style={styles.checkbox}
        trackingData={{
          name: 'edit_note_action_sheet.persist_notes_checkbox',
          properties: {
            variantPackUUID: orderGuideItem.variant_pack?.uuid,
          },
        }}
      />
      <Button
        containerStyle={styles.saveButtonContainer}
        disabled={!noteValue}
        loading={isLoading}
        onPress={async () => {
          try {
            setIsLoading(true);
            await onSave(noteValue, persistNotes);
            onClose();
          } catch (e) {
            showErrorToast();
            setIsLoading(false);
          }
        }}
        trackingData={{
          name: 'edit_note_action_sheet.save_button',
          properties: {
            variantPackUUID: orderGuideItem.variant_pack?.uuid,
          },
        }}
      >
        {i18n.t('save')}
      </Button>
      {showDeleteButton ? (
        <Button
          containerStyle={styles.deleteButtonContainer}
          loading={deleteLoading}
          onPress={async () => {
            try {
              setDeleteLoading(true);
              await onSave(null, persistNotes);
              onClose();
            } catch (e) {
              showErrorToast();
              setDeleteLoading(false);
            }
          }}
          trackingData={{
            name: 'edit_note_action_sheet.delete_button',
            properties: {
              variantPackUUID: orderGuideItem.variant_pack?.uuid,
            },
          }}
          type="danger"
        >
          {i18n.t('delete_note')}
        </Button>
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  checkbox: {
    paddingLeft: 0,
    paddingVertical: 0,
  },
  deleteButtonContainer: {
    marginTop: 16,
  },
  inputCaptionText: {
    textAlign: 'right',
  },
  inputCaptionTextError: {
    color: theme.colors.negative,
  },
  inputContainer: {
    borderColor: theme.colors.stroke,
    borderWidth: 1,
  },
  saveButtonContainer: {
    marginTop: 16,
  },
  subtitle: {
    marginBottom: 16,
  },
  subtitleLarge: {
    textAlign: 'center',
  },
}));
